/* eslint-disable jsx-a11y/no-interactive-element-to-noninteractive-role */
import React, { useEffect, useRef } from 'react';
import { useSpring, animated } from 'react-spring';
import { useInView } from 'react-intersection-observer';
import { BLOCKS } from '@contentful/rich-text-types';
import cx from 'classnames';

import { IPlansTable30Fields } from '../../../types/contentful';

import RichText from '../RichText';
import PlansIcon from './PlansIcon';

import { FeatureRow } from './styles';
import { ScrollReveal } from '../ScrollReveal';

import plansStar from '@/images/plans-star.svg';
import Image from 'next/image';

import { Features, processPlans } from '../PlansTable/helper';
import { normalizeHexCode } from '~/styles/utils';
import { getImageData } from '~/utils/contentful';
import { EmbeddedEntryNode } from '../RichText/Nodes';
import { trackWithFingerprint } from '~/utils/trackWithFingerprint';
import { SmartText } from '~/components/SmartText';
import { usePageDataContext } from '~/context/PageData';
import { getCookie } from '~/utils/cookies';

const ArrowIcon = () => (
  <svg
    className={'mt-[3px]'}
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M10.4419 4.81694C10.1979 4.57286 9.80214 4.57286 9.55806 4.81694L9.45014 4.92486C9.20622 5.16878 9.20604 5.56421 9.44974 5.80835L12.932 9.29687H5C4.65482 9.29687 4.375 9.5767 4.375 9.92187V10.0781C4.375 10.4233 4.65482 10.7031 5 10.7031H12.932L9.44974 14.1917C9.20604 14.4358 9.20622 14.8312 9.45014 15.0751L9.55806 15.1831C9.80214 15.4271 10.1979 15.4271 10.4419 15.1831L15.1831 10.4419C15.4271 10.1979 15.4271 9.80214 15.1831 9.55806L10.4419 4.81694Z"
      fill="#1D252C"
    />
  </svg>
);

function useMirrorScroll(
  source: React.RefObject<HTMLElement>,
  target: React.RefObject<HTMLElement>
) {
  useEffect(() => {
    if (!source.current || !target.current) return;
    const sync = () =>
      (target.current!.scrollLeft = source.current!.scrollLeft);
    const sourceEl = source.current;
    sourceEl.addEventListener('scroll', sync, { passive: true });
    return () => {
      if (sourceEl) sourceEl.removeEventListener('scroll', sync);
    };
  }, [source, target]);
}

export function PlansTierTable({
  title,
  backgroundColor,
  plans = [],
  priceOrder,
  helperCopyInputText,
  helperCopyToggle,
  redesign20Toggle
}: IPlansTable30Fields) {
  const {
    page: { hideBanner, pageWebBanner } = {},
    cookies: {
      gl_web_banner: glWebBanner,
      gl_is_registered_user: registeredUser
    } = {}
  } = usePageDataContext();

  const [isBannerVisible, setIsBannerVisible] = React.useState<boolean>(
    glWebBanner === 'show' &&
      registeredUser !== 'true' &&
      hideBanner !== true &&
      !!pageWebBanner
  );

  // Poll for gl_web_banner cookie every 1s
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const interval = setInterval(() => {
      const value = getCookie('gl_web_banner');
      setIsBannerVisible(value === 'show');
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.5
  });

  const nudgeProps = useSpring({
    to: inView
      ? [
          { transform: 'translateX(4px)' },
          { transform: 'translateX(0px)' },
          { transform: 'translateX(4px)' },
          { transform: 'translateX(0px)' }
        ]
      : { transform: 'translateX(0px)' },
    from: { transform: 'translateX(0px)' },
    config: { duration: 400 }
  });

  const tableRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useMirrorScroll(tableRef, mobileRef);

  const handleScrollRight = () => {
    if (tableRef.current) {
      tableRef.current.scrollTo({
        left: tableRef.current.scrollWidth - tableRef.current.clientWidth,
        behavior: 'smooth'
      });
    }
  };

  if (!plans) {
    return null;
  }

  const allFeatures: Features = processPlans(plans);

  const plansToRender = priceOrder ? plans : plans.slice().reverse();

  const toggleText = helperCopyInputText || (priceOrder ? 'Infinity' : 'Core');
  if (!redesign20Toggle) {
    return (
      <ScrollReveal className="block-container relative">
        {helperCopyToggle && (
          <span
            className="body-small mobile-core-nudge"
            data-testid="mobile-core-nudge"
            role="button"
            tabIndex={0}
            onClick={handleScrollRight}
            onKeyDown={handleScrollRight}
            style={{ cursor: 'pointer' }}
          >
            {toggleText}
            <animated.span ref={ref} style={nudgeProps}>
              <ArrowIcon />
            </animated.span>
          </span>
        )}
        <div
          ref={tableRef}
          className="layout layout-padding plans-table-container overflow-x-auto overflow-y-hidden pb-10"
          style={{ backgroundColor: backgroundColor ?? 'transparent' }}
          data-testid="plans-table"
        >
          <div className="box-column w-full min-w-[452px] pt-0 tablet:w-11/12 desktop:pt-1">
            <table
              className="relative w-full border-collapse"
              style={{ tableLayout: 'fixed' }}
            >
              <colgroup>
                <col className="sticky left-[0] z-[2] w-[137px] tablet:static tablet:w-[220px] desktop:w-[264px] max:w-[448px]" />
                {(plansToRender || []).map((_, index) => (
                  <col key={index} className="min-w-25 desktop:w-full" />
                ))}
              </colgroup>
              <thead className="relative">
                <tr className="relative">
                  <th
                    className="sticky left-[0] z-[2] w-full max-w-[416px] px-4 pb-0 tablet:static tablet:w-5/12 tablet:pl-0"
                    style={{
                      backgroundColor: backgroundColor ?? 'white'
                    }}
                  >
                    <h2
                      className="heading-2 flex justify-center text-left tablet:justify-normal"
                      data-blockid="plansTable-desktop-title"
                    >
                      {title}
                    </h2>
                  </th>
                  {(plansToRender || []).map(
                    ({ sys, fields }, planHeaderIndex) => (
                      <React.Fragment key={sys?.id}>
                        <th
                          key={sys?.id}
                          className="relative w-25 tablet:w-auto"
                          scope="col"
                          id={`plan-header-${planHeaderIndex}`}
                        >
                          <div
                            className={cx(
                              'align-center relative z-[1] mt-1 flex h-full max-h-32 min-h-25 w-full max-w-[250px] flex-col justify-center rounded-t-xl px-1 tablet:mx-5',
                              { '-mb-1 bg-white': fields?.isRecommended }
                            )}
                          >
                            {fields?.isRecommended && (
                              <div
                                className="absolute z-[1] -mt-[4.5rem] flex h-8 w-full max-w-[250px] items-center justify-center rounded-pill py-1"
                                style={{
                                  color: 'white',
                                  backgroundColor: normalizeHexCode(
                                    fields?.recommendedHighlightColor
                                  )
                                }}
                              >
                                <Image src={plansStar} alt="Plans Star" />
                                <RichText
                                  data={fields?.recommendedHighlightCopy}
                                  plansTierStyling={true}
                                  overrideOptions={{
                                    [BLOCKS.PARAGRAPH]: (_node, children) => (
                                      <p className="ml-1 text-center text-sm font-medium leading-normal">
                                        {children}
                                      </p>
                                    ),
                                    [BLOCKS.EMBEDDED_ASSET]: (node) => (
                                      <Image
                                        className="mr-1 !size-4"
                                        {...getImageData(node.data.target)}
                                      />
                                    )
                                  }}
                                />
                              </div>
                            )}
                            <h3
                              className="mx-auto mt-5 flex w-full justify-center whitespace-nowrap text-wrap rounded-full px-[6px] text-center text-2xl font-medium leading-[30px] tiny:max-w-[98%] tablet:max-w-[300px]"
                              data-blockid="plansTable-desktop-title"
                              style={{
                                color: fields?.textColor ?? '#000000'
                              }}
                            >
                              {fields?.name}
                            </h3>
                            {fields?.subheaderRichTextOption ? (
                              <div
                                className="body-small !mx-auto pt-2 text-center desktop:pt-4 [&>p]:text-nowrap [&>p]:font-medium"
                                data-testid="subheader-rich-text"
                              >
                                <RichText
                                  bulletColor="white"
                                  data={fields?.subheaderRichTextOption}
                                  plansTierStyling={true}
                                />
                              </div>
                            ) : (
                              fields.subheader && (
                                <p
                                  className="body-small pt-2 text-center"
                                  data-testid="subheader-string"
                                >
                                  {fields?.subheader}
                                </p>
                              )
                            )}
                          </div>
                        </th>
                      </React.Fragment>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="plans-table-body">
                {allFeatures.map((feature, index: number) => (
                  <FeatureRow key={index}>
                    <th
                      className={cx(
                        'sticky left-[0] z-[1] my-2 px-4 text-left tablet:pl-0',
                        { 'border-none pt-5': index === allFeatures.length - 1 }
                      )}
                      style={{
                        backgroundColor: backgroundColor ?? 'white'
                      }}
                      scope="row"
                      id={`feature-header-${index}`}
                    >
                      <SmartText>
                        <h3 className="font-medium leading-[150%] desktop:max-w-72">
                          {feature && feature.title}
                        </h3>
                      </SmartText>
                    </th>
                    {plansToRender?.map(({ fields }, planIndex: number) => {
                      const isInPlan = priceOrder
                        ? feature.includedInNumberOfPlans >=
                          plansToRender.length - planIndex
                        : feature.includedInNumberOfPlans >= planIndex + 1;

                      const copy = feature.copies.get(fields.name ?? '');

                      return (
                        <td
                          className={cx('text-center', {
                            'rounded-b-md border-none':
                              index === allFeatures.length - 1
                          })}
                          key={`${index}-${planIndex}`}
                          headers={`feature-header-${index} plan-header-${planIndex}`}
                        >
                          <div
                            className={cx(
                              'min-h-20 w-full max-w-[250px] tiny:-mb-3 tiny:h-28 tablet:mx-5 tablet:-mb-1',
                              {
                                'rounded-b-md tiny:-mt-2':
                                  index === allFeatures.length - 1,
                                'tiny:bg-white': fields?.isRecommended
                              },
                              {
                                'flex flex-col items-center justify-center tablet:h-full tablet:py-3':
                                  copy
                              },
                              { 'tiny:pt-10 tablet:h-20 tablet:pt-7': !copy }
                            )}
                          >
                            <div
                              className={cx(
                                'mx-auto flex items-center justify-center rounded-full',
                                {
                                  'h-full w-full max-w-[250px]': copy,
                                  'w-7': !copy
                                }
                              )}
                            >
                              {isInPlan ? (
                                <PlansIcon
                                  name={fields?.name as string}
                                  title={feature?.title as string}
                                  iconColor={fields?.textColor}
                                  replaceWithText={copy}
                                  textClasses="text-base font-medium text-[#1D252C] max-w-[200px]"
                                />
                              ) : (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="18"
                                  height="28"
                                  viewBox="0 0 18 17"
                                  fill="none"
                                  aria-label={`${feature?.title} not included in ${fields?.name}`}
                                >
                                  <path
                                    d="M1.55566 1L15.6978 15.1421"
                                    stroke="#B4B6B9"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                  />
                                  <path
                                    d="M15.7686 1.07129L1.62642 15.2134"
                                    stroke="#B4B6B9"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              )}
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </FeatureRow>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </ScrollReveal>
    );
  } else {
    return (
      <ScrollReveal className="block-container flex flex-col items-center justify-center pl-5 tablet:pl-0">
        <div
          className={cx(
            'layout layout-padding plans-table-container20 sticky z-[3] flex items-center overflow-hidden rounded-b-none !pb-0 tablet:!px-4 desktop:!px-8',
            isBannerVisible
              ? 'top-[101px] tablet:top-[106px] desktop:top-[121px]'
              : 'top-[45px] tablet:top-[50px] desktop:top-[66px]'
          )}
          style={{
            backgroundColor: backgroundColor ?? 'transparent'
          }}
        >
          {helperCopyToggle && (
            <span
              className="body-small mobile-core-nudge"
              data-testid="mobile-core-nudge"
              role="button"
              tabIndex={0}
              onClick={handleScrollRight}
              onKeyDown={handleScrollRight}
              style={{ cursor: 'pointer' }}
            >
              {toggleText}
              <animated.span ref={ref} style={nudgeProps}>
                <ArrowIcon />
              </animated.span>
            </span>
          )}
          {/* The main grid wrapper: one column for the label or feature, plus one column for each plan. */}
          <div
            ref={mobileRef}
            className="invisible-scroll flex-1 touch-none select-none overflow-x-hidden tablet:self-stretch tablet:!pr-none"
          >
            <div
              className="box-column plans-table20-grid w-full min-w-[452px] pt-0"
              style={
                {
                  display: 'grid',
                  gridAutoRows: 'auto',
                  '--num-plans': plansToRender.length
                } as React.CSSProperties
              }
            >
              {/* ============= HEADER ROW ============= */}
              {/* First cell in row 1, column 1: "Plans" title */}
              <div
                style={{
                  gridColumn: '1 / 2',
                  gridRow: '1 / 2',
                  backgroundColor: backgroundColor ?? 'white'
                }}
                className={cx(
                  'sticky left-0  z-[3] flex flex-col justify-end py-4 pl-4 tablet:pl-0',
                  isBannerVisible ? 'top-[111px]' : 'top-[55px]'
                )}
              >
                <h2
                  className="typography-display-4 text-left"
                  data-blockid="plansTable-desktop-title"
                  data-testid="redesign20-header"
                >
                  {title}
                </h2>
              </div>

              {/* Next cells in row 1, columns 2..N: each plan header */}
              {plansToRender.map(({ sys, fields }, index) => (
                <div
                  key={sys?.id}
                  style={{
                    gridColumn: `${index + 2} / ${index + 3}`,
                    gridRow: '1 / 2'
                  }}
                  className="relative h-full"
                >
                  <div
                    className={cx(
                      'flex h-full flex-col justify-center rounded-t-md pl-4 desktop:pl-6'
                    )}
                  >
                    <div
                      className={cx(
                        'flex h-full w-full flex-col justify-center rounded-t-md bg-white pt-5',
                        {
                          'min-w-[136px] desktop:min-w-[179px] max:min-w-[264px]':
                            plansToRender.length === 3
                        },
                        { 'min-w-[110px]': plansToRender.length === 4 }
                      )}
                    >
                      {/* Plan Name */}
                      <h3
                        className="typography-display-4 my-auto flex w-full items-center justify-center text-wrap rounded-full px-1 text-center"
                        data-blockid="plansTable-desktop-title"
                      >
                        {fields?.name}
                      </h3>
                      {/* Subheader */}
                      {fields?.subheaderRichTextOption ? (
                        <div
                          className="typography-body-2 max:typography-body-1 !mx-auto mt-auto w-full items-end px-3 py-2 text-center desktop:px-4 desktop:pt-4 [&>a]:mt-4 [&>a]:w-full [&>a]:min-w-none"
                          data-testid="subheader-rich-text"
                        >
                          <RichText
                            bulletColor="white"
                            data={fields?.subheaderRichTextOption}
                            plansTierStyling={true}
                            overrideOptions={{
                              [BLOCKS.EMBEDDED_ENTRY]: (node, children) => (
                                <EmbeddedEntryNode
                                  node={node}
                                  colour="defaultText"
                                  func={async () => {
                                    await trackWithFingerprint('CTA Clicked', {
                                      module: 'plans_table',
                                      page_url: window.location.href,
                                      value: fields.name
                                    });
                                  }}
                                >
                                  {children}
                                </EmbeddedEntryNode>
                              )
                            }}
                          />
                        </div>
                      ) : (
                        fields.subheader && (
                          <p
                            className="typography-body-2 max:typography-body-1 items-end py-2 text-center"
                            data-testid="subheader-string"
                          >
                            {fields?.subheader}
                          </p>
                        )
                      )}
                    </div>
                  </div>
                </div>
              ))}

              <div className="plans-spacer tablet:hidden" />
            </div>
          </div>
        </div>
        <div
          ref={tableRef}
          className="
              layout 
              layout-padding
              plans-table-container20 
              tablet:overflow-initial 
              snap-x
              snap-mandatory 
              overflow-x-auto 
              scroll-smooth 
              rounded-t-none 
              !pt-0
              pb-4
              tablet:!px-4
              tablet:pb-8 
              desktop:!px-8
            "
          style={{
            backgroundColor: backgroundColor ?? 'transparent'
          }}
          data-testid="plans-table-redesign20"
        >
          {/* The main grid wrapper: one column for the label or feature, plus one column for each plan. */}
          <div
            className="box-column plans-table20-grid w-full min-w-[452px] pt-0"
            style={
              {
                display: 'grid',
                gridAutoRows: 'auto',
                '--num-plans': plansToRender.length
              } as React.CSSProperties
            }
          >
            {/* ============= FEATURE ROWS ============= */}
            {allFeatures.map((feature, featureIndex) => {
              // Each row after the first is a feature row
              // The row = featureIndex + 2 (since row 1 is the header)
              const rowNumber = featureIndex + 2;

              const isLastRow = featureIndex === allFeatures.length - 1;

              return (
                <React.Fragment key={featureIndex}>
                  {/* Feature name in the first column of this row */}
                  <div
                    style={{
                      gridColumn: '1 / 2',
                      gridRow: rowNumber,
                      background: backgroundColor ?? 'transparent'
                    }}
                    className={cx(
                      'flex min-h-[72px] items-center py-1 pr-4 tablet:py-[22.5px] desktop:h-16 desktop:max-h-none',
                      {
                        'border-none': featureIndex === allFeatures.length - 1
                      },
                      { 'border-b border-[#dbdbdb]': !isLastRow },
                      'sticky left-0 z-[2] pl-4 tablet:pl-0'
                    )}
                  >
                    <SmartText>
                      <h3 className="text-sm leading-[125%] desktop:text-base desktop:leading-[120%]">
                        {feature?.title}
                      </h3>
                    </SmartText>
                  </div>

                  {/* Then one cell per plan for checkmarks, columns 2..N in this same row */}
                  {plansToRender.map(({ fields }, planIndex) => {
                    const colNumber = planIndex + 2;
                    const isInPlan = priceOrder
                      ? feature.includedInNumberOfPlans >=
                        plansToRender.length - planIndex
                      : feature.includedInNumberOfPlans >= planIndex + 1;

                    const copy = feature.copies.get(fields.name ?? '');

                    return (
                      <div
                        key={`${featureIndex}-${planIndex}`}
                        style={{
                          gridColumn: `${colNumber} / ${colNumber + 1}`,
                          gridRow: rowNumber
                        }}
                        className={cx('pl-4 text-center desktop:pl-6', {
                          'rounded-b-md border-none': isLastRow,
                          'border-b border-[#dbdbdb] bg-clip-content':
                            !isLastRow
                        })}
                      >
                        <div
                          className={cx(
                            'w-full bg-white tiny:-mb-3 tablet:-mb-1',
                            {
                              'rounded-b-md': isLastRow,
                              'tiny:bg-white': fields?.isRecommended
                            },
                            {
                              'flex flex-col items-center justify-center tablet:h-full':
                                copy
                            },
                            'flex h-full w-full flex-col justify-center bg-white',
                            {
                              'min-w-[136px] desktop:min-w-[179px] max:min-w-[264px]':
                                plansToRender.length === 3
                            },
                            { 'min-w-[110px]': plansToRender.length === 4 }
                          )}
                        >
                          {isInPlan ? (
                            <PlansIcon
                              name={fields?.name as string}
                              title={feature?.title as string}
                              replaceWithText={copy}
                              redesign20
                              iconColor={fields.textColor}
                              textClasses={'text-xl leading-[32px] font-medium'}
                              iconClasses={'size-[29px] mx-auto'}
                            />
                          ) : (
                            // The X icon for a feature not included
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              className={'mx-auto size-5'}
                              height="28"
                              viewBox="0 0 18 17"
                              fill="none"
                              aria-label={`${feature?.title} not included in ${fields?.name}`}
                            >
                              <path
                                d="M1.55566 1L15.6978 15.1421"
                                stroke="#B4B6B9"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                              <path
                                d="M15.7686 1.07129L1.62642 15.2134"
                                stroke="#B4B6B9"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                            </svg>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div className="plans-spacer tablet:hidden" />
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </ScrollReveal>
    );
  }
}
